import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { getAllCentersHealth } from '../../services/centerMembershipService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const healthColors = {
  green: { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  red: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

const roleBadgeColors = {
  super_admin: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  center_manager: 'bg-teal-100 text-teal-700',
  sales: 'bg-orange-100 text-orange-700',
  operations: 'bg-gray-100 text-gray-700',
  none: 'bg-gray-100 text-gray-500',
};

function StatCard({ icon, label, value, sub, color = 'text-foreground', iconBg = 'bg-primary/10', iconColor = 'text-primary' }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon name={icon} size={22} className={iconColor} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
        <p className={`text-2xl font-bold mt-0.5 ${color}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const SuperAdminDashboard = () => {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Data states
  const [centersHealth, setCentersHealth] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [expiries, setExpiries] = useState({ d30: [], d60: [], d90: [] });
  const [users, setUsers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([loadCentersHealth(), loadGlobalStats(), loadExpiries(), loadUsers()]);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadCentersHealth = async () => {
    const data = await getAllCentersHealth();
    setCentersHealth(data || []);
  };

  const loadGlobalStats = async () => {
    try {
      // Inventory totals
      const { data: inv } = await supabase
        ?.from('inventory')
        ?.select('capacity_seats, area_sqft, billing_metric, status, center_id');

      const totalSeats = inv?.reduce((s, u) => s + (u?.capacity_seats || 0), 0) || 0;
      const totalSqft = inv?.reduce((s, u) => s + (u?.area_sqft || 0), 0) || 0;
      const bookedSeats = inv?.filter(u => u?.status === 'booked')?.reduce((s, u) => s + (u?.capacity_seats || 0), 0) || 0;
      const bookedSqft = inv?.filter(u => u?.status === 'booked')?.reduce((s, u) => s + (u?.area_sqft || 0), 0) || 0;
      const occupancyPct = totalSeats > 0 ? Math.round((bookedSeats / totalSeats) * 100) : 0;

      // Centers count
      const { count: centersCount } = await supabase?.from('centers')?.select('id', { count: 'exact', head: true })?.eq('is_active', true);

      // Leads count
      const { count: activeLeads } = await supabase?.from('leads')?.select('id', { count: 'exact', head: true })?.not('stage', 'in', '(won,lost)');

      setGlobalStats({
        totalSeats,
        totalSqft,
        bookedSeats,
        bookedSqft,
        occupancyPct,
        centersCount: centersCount || 0,
        activeLeads: activeLeads || 0,
        totalUnits: inv?.length || 0,
      });
    } catch (err) {
      console.error('loadGlobalStats error:', err);
    }
  };

  const loadExpiries = async () => {
    try {
      const now = new Date();
      const d30 = new Date(now); d30?.setDate(d30?.getDate() + 30);
      const d60 = new Date(now); d60?.setDate(d60?.getDate() + 60);
      const d90 = new Date(now); d90?.setDate(d90?.getDate() + 90);

      const { data } = await supabase
        ?.from('agreements')
        ?.select('id, tenant_name, end_date, rent_amount, billing_type, booked_seats, booked_sqft, centers(name)')
        ?.gte('end_date', now?.toISOString()?.split('T')?.[0])
        ?.lte('end_date', d90?.toISOString()?.split('T')?.[0])
        ?.eq('status', 'active')
        ?.order('end_date', { ascending: true });

      const records = data || [];
      const nowStr = now?.toISOString()?.split('T')?.[0];
      const d30Str = d30?.toISOString()?.split('T')?.[0];
      const d60Str = d60?.toISOString()?.split('T')?.[0];
      const d90Str = d90?.toISOString()?.split('T')?.[0];

      setExpiries({
        d30: records?.filter(r => r?.end_date <= d30Str),
        d60: records?.filter(r => r?.end_date > d30Str && r?.end_date <= d60Str),
        d90: records?.filter(r => r?.end_date > d60Str && r?.end_date <= d90Str),
      });
    } catch (err) {
      console.error('loadExpiries error:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const { data: profiles } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, phone, role, global_role, is_active, created_at')
        ?.order('created_at', { ascending: false });

      const { data: mems } = await supabase
        ?.from('center_memberships')
        ?.select('id, user_id, center_id, role, centers(id, name, city)');

      setUsers(profiles || []);
      setMemberships(mems || []);
    } catch (err) {
      console.error('loadUsers error:', err);
    }
  };

  // ─── Derived data ──────────────────────────────────────────────────────────
  const filteredUsers = users?.filter(u => {
    const matchSearch = !userSearch ||
      u?.full_name?.toLowerCase()?.includes(userSearch?.toLowerCase()) ||
      u?.email?.toLowerCase()?.includes(userSearch?.toLowerCase());
    const matchRole = userRoleFilter === 'all' ||
      u?.global_role === userRoleFilter ||
      u?.role === userRoleFilter;
    return matchSearch && matchRole;
  });

  const getUserMemberships = (userId) =>
    memberships?.filter(m => m?.user_id === userId);

  const healthSummary = {
    green: centersHealth?.filter(c => c?.health?.health_label === 'green')?.length || 0,
    yellow: centersHealth?.filter(c => c?.health?.health_label === 'yellow')?.length || 0,
    red: centersHealth?.filter(c => c?.health?.health_label === 'red')?.length || 0,
  };

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" size={40} className="animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">Loading Super Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Super Admin Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Global overview across all centers</p>
        </div>
        <button
          onClick={loadAll}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
        >
          <Icon name="RefreshCw" size={15} />
          Refresh
        </button>
      </div>
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-sm flex items-center gap-2">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}
      {/* Global KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon="Building"
          label="Total Centers"
          value={globalStats?.centersCount ?? '—'}
          sub={`${healthSummary?.green} healthy · ${healthSummary?.yellow} moderate · ${healthSummary?.red} at risk`}
          iconBg="bg-purple-50"
          iconColor="text-purple-600"
        />
        <StatCard
          icon="Armchair"
          label="Total Seats"
          value={globalStats?.totalSeats?.toLocaleString() ?? '—'}
          sub={`${globalStats?.bookedSeats?.toLocaleString()} booked`}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
        <StatCard
          icon="SquareStack"
          label="Total Sqft"
          value={globalStats?.totalSqft ? `${(globalStats?.totalSqft / 1000)?.toFixed(1)}k` : '—'}
          sub={`${globalStats?.bookedSqft ? (globalStats?.bookedSqft / 1000)?.toFixed(1) + 'k' : 0} booked`}
          iconBg="bg-teal-50"
          iconColor="text-teal-600"
        />
        <StatCard
          icon="TrendingUp"
          label="Global Occupancy"
          value={`${globalStats?.occupancyPct ?? 0}%`}
          sub={`${globalStats?.totalUnits} total units`}
          color={globalStats?.occupancyPct >= 80 ? 'text-green-600' : globalStats?.occupancyPct >= 50 ? 'text-yellow-600' : 'text-red-600'}
          iconBg="bg-green-50"
          iconColor="text-green-600"
        />
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-1">
          {[
            { key: 'overview', label: 'Centers Overview', icon: 'Building' },
            { key: 'expiries', label: 'Upcoming Expiries', icon: 'CalendarClock' },
            { key: 'users', label: 'User Management', icon: 'Users' },
          ]?.map(tab => (
            <button
              key={tab?.key}
              onClick={() => setActiveTab(tab?.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.key
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      {/* ── Tab: Centers Overview ── */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* Health summary pills */}
          <div className="flex gap-3 flex-wrap">
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs font-medium text-green-700">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
              {healthSummary?.green} Healthy
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-full text-xs font-medium text-yellow-700">
              <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />
              {healthSummary?.yellow} Moderate Risk
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-full text-xs font-medium text-red-700">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
              {healthSummary?.red} High Risk
            </span>
          </div>

          {centersHealth?.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <Icon name="Building" size={40} className="mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">No centers found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {centersHealth?.map(center => {
                const label = center?.health?.health_label || 'red';
                const colors = healthColors?.[label] || healthColors?.red;
                const score = center?.health?.health_score || 0;
                const forecast = center?.forecast || {};

                return (
                  <div key={center?.id} className={`border rounded-xl p-5 ${colors?.bg} ${colors?.border}`}>
                    {/* Center header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${colors?.dot}`} />
                          <h3 className="font-semibold text-foreground">{center?.name}</h3>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 ml-4">{center?.city}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${colors?.badge}`}>
                          {label}
                        </span>
                        <p className="text-xl font-bold text-foreground mt-1">{score}<span className="text-xs font-normal text-muted-foreground">/100</span></p>
                      </div>
                    </div>

                    {/* Health score breakdown */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[
                        { label: 'Occupancy', value: center?.health?.occupancy_score || 0 },
                        { label: 'Pipeline', value: center?.health?.pipeline_score || 0 },
                        { label: 'Expiry', value: center?.health?.expiry_score || 0 },
                      ]?.map(item => (
                        <div key={item?.label} className="bg-white/70 rounded-lg p-2.5 text-center">
                          <p className="text-sm font-bold text-foreground">{item?.value}</p>
                          <p className="text-xs text-muted-foreground">{item?.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Forecast row */}
                    {forecast?.forecast_seats !== undefined && (
                      <div className="bg-white/50 rounded-lg px-3 py-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span>Booked: <strong className="text-foreground">{forecast?.booked_seats ?? 0} seats</strong></span>
                        <span>Pipeline: <strong className="text-foreground">+{forecast?.pipeline_weighted_seats ?? 0}</strong></span>
                        <span>Forecast (90d): <strong className="text-primary">{forecast?.forecast_seats ?? 0} seats</strong></span>
                        <span>Occupancy: <strong className="text-foreground">{forecast?.occupancy_pct ?? 0}%</strong></span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* ── Tab: Upcoming Expiries ── */}
      {activeTab === 'expiries' && (
        <div className="space-y-6">
          {/* Summary row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-red-700">{expiries?.d30?.length}</p>
              <p className="text-xs text-red-600 mt-1 font-medium">Expiring in 30 days</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-orange-700">{expiries?.d60?.length}</p>
              <p className="text-xs text-orange-600 mt-1 font-medium">31–60 days</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-yellow-700">{expiries?.d90?.length}</p>
              <p className="text-xs text-yellow-600 mt-1 font-medium">61–90 days</p>
            </div>
          </div>

          {/* Expiry buckets */}
          {[
            { key: 'd30', label: '⚠️ Expiring within 30 days', records: expiries?.d30, color: 'border-red-200 bg-red-50', badgeColor: 'bg-red-100 text-red-700' },
            { key: 'd60', label: '🔶 Expiring in 31–60 days', records: expiries?.d60, color: 'border-orange-200 bg-orange-50', badgeColor: 'bg-orange-100 text-orange-700' },
            { key: 'd90', label: '🟡 Expiring in 61–90 days', records: expiries?.d90, color: 'border-yellow-200 bg-yellow-50', badgeColor: 'bg-yellow-100 text-yellow-700' },
          ]?.map(bucket => (
            <div key={bucket?.key}>
              <h3 className="text-sm font-semibold text-foreground mb-3">{bucket?.label}</h3>
              {bucket?.records?.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <Icon name="CheckCircle" size={24} className="mx-auto mb-2 text-green-500" />
                  <p className="text-sm text-muted-foreground">No agreements expiring in this window</p>
                </div>
              ) : (
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Tenant</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Center</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Expiry Date</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Billing</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Rent/mo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {bucket?.records?.map(r => (
                        <tr key={r?.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground">{r?.tenant_name || '—'}</td>
                          <td className="px-4 py-3 text-muted-foreground">{r?.centers?.name || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${bucket?.badgeColor}`}>
                              {r?.end_date}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground capitalize">
                            {r?.billing_type === 'seat'
                              ? `${r?.booked_seats || 0} seats`
                              : `${r?.booked_sqft || 0} sqft`}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-foreground">
                            {r?.rent_amount ? `₹${Number(r?.rent_amount)?.toLocaleString()}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* ── Tab: User Management ── */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={e => setUserSearch(e?.target?.value)}
                className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <select
              value={userRoleFilter}
              onChange={e => setUserRoleFilter(e?.target?.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="all">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="center_manager">Center Manager</option>
              <option value="sales">Sales</option>
              <option value="operations">Operations</option>
            </select>
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card border border-border rounded-lg px-3 py-2">
              <Icon name="Users" size={15} />
              <span>{filteredUsers?.length} users</span>
            </div>
          </div>

          {/* Users table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Global Role</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Center Memberships</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUsers?.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">
                      <Icon name="Users" size={28} className="mx-auto mb-2 opacity-30" />
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers?.map(u => {
                    const userMems = getUserMemberships(u?.id);
                    const displayRole = u?.global_role === 'super_admin' ? 'super_admin' : (u?.role || 'none');
                    const roleColor = roleBadgeColors?.[displayRole] || roleBadgeColors?.none;

                    return (
                      <tr key={u?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-primary">
                                {u?.full_name?.charAt(0)?.toUpperCase() || u?.email?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate">{u?.full_name || '—'}</p>
                              <p className="text-xs text-muted-foreground truncate">{u?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${roleColor}`}>
                            {displayRole?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {userMems?.length === 0 ? (
                            <span className="text-xs text-muted-foreground italic">No center memberships</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {userMems?.map(m => (
                                <span
                                  key={m?.id}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded-full text-xs text-foreground"
                                  title={`Role: ${m?.role}`}
                                >
                                  <Icon name="Building" size={10} className="text-muted-foreground" />
                                  {m?.centers?.name || m?.center_id}
                                  <span className={`ml-0.5 px-1 rounded text-xs ${roleBadgeColors?.[m?.role] || roleBadgeColors?.none}`}>
                                    {m?.role}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            u?.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {u?.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Membership summary by center */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Membership Distribution by Center</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.from(new Set(memberships?.map(m => m?.center_id)))?.map(centerId => {
                const centerMems = memberships?.filter(m => m?.center_id === centerId);
                const centerName = centerMems?.[0]?.centers?.name || centerId;
                const roleGroups = centerMems?.reduce((acc, m) => {
                  acc[m?.role] = (acc?.[m?.role] || 0) + 1;
                  return acc;
                }, {});

                return (
                  <div key={centerId} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="Building" size={16} className="text-primary" />
                      <p className="font-medium text-foreground text-sm">{centerName}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(roleGroups)?.map(([role, count]) => (
                        <span
                          key={role}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleBadgeColors?.[role] || roleBadgeColors?.none}`}
                        >
                          {count} {role?.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{centerMems?.length} total members</p>
                  </div>
                );
              })}
              {memberships?.length === 0 && (
                <div className="col-span-3 bg-card border border-border rounded-xl p-8 text-center">
                  <Icon name="Users" size={28} className="mx-auto mb-2 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground text-sm">No center memberships found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
